import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '@/lib/api';

const AuthContext = createContext(null);

// Role constants
export const ROLES = {
  SUPERADMIN: 'superadmin',
  KOMMUN_ADMIN: 'kommun_admin',
  SCHOOL_ADMIN: 'school_admin',
  STAFF: 'staff'
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await authApi.me();
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      localStorage.removeItem('token');
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (email, password) => {
    const response = await authApi.login({ email, password });
    localStorage.setItem('token', response.data.access_token);
    await loadUser();
    return response;
  };

  const register = async (data) => {
    const response = await authApi.register(data);
    localStorage.setItem('token', response.data.access_token);
    await loadUser();
    return response;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setIsAuthenticated(false);
  };

  // Role-based access helpers
  const isSuperAdmin = () => user?.role === ROLES.SUPERADMIN;
  const isKommunAdmin = () => user?.role === ROLES.KOMMUN_ADMIN;
  const isSchoolAdmin = () => user?.role === ROLES.SCHOOL_ADMIN;
  const isStaff = () => user?.role === ROLES.STAFF;
  
  // Permission helpers
  const canCreate = () => user?.role === ROLES.SUPERADMIN || user?.role === ROLES.SCHOOL_ADMIN;
  const canUpdate = () => user?.role === ROLES.SUPERADMIN || user?.role === ROLES.SCHOOL_ADMIN;
  const canDelete = () => user?.role === ROLES.SUPERADMIN || user?.role === ROLES.SCHOOL_ADMIN;
  const canManageUsers = () => user?.role === ROLES.SUPERADMIN || user?.role === ROLES.SCHOOL_ADMIN;
  const canManageEvents = () => true;

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      isAuthenticated,
      token: localStorage.getItem('token'),
      login, 
      register, 
      logout,
      // Role checks
      isSuperAdmin,
      isKommunAdmin,
      isSchoolAdmin,
      isStaff,
      // Permission checks
      canCreate,
      canUpdate,
      canDelete,
      canManageUsers,
      canManageEvents
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
